import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { Scale } from '~/shared/graphql/queries/useScalesQuery';
import {
  $globalScales,
  $globalScalesError,
  $globalScalesLoading,
} from '~/shared/state/scales.state';

const MAX_FAVORITE_SCALES = 3;

export const $scaleId = atomWithStorage<string | null>('trade/scaleId', null);
export const $favoriteScaleIds = atomWithStorage<string[]>(
  'trade/favoriteScaleIds',
  [],
);
export const $showScaleCamera = atomWithStorage<boolean>(
  'trade/showScaleCamera',
  true,
);

export type LabeledScale = Scale & {
  shortName: string;
  isFavorite: boolean;
};

export const $rawScales = $globalScales;

export const $scalesLoading = $globalScalesLoading;

export const $scalesError = $globalScalesError;

export const $validFavoriteScaleIds = atom((get) => {
  const rawScales = get($rawScales);
  const favoriteScaleIds = get($favoriteScaleIds);

  return favoriteScaleIds.filter((id) =>
    rawScales.some((scale) => scale.id === id),
  );
});

export const $labeledScales = atom<LabeledScale[]>((get) => {
  const rawScales = get($rawScales);
  const validFavoriteScaleIds = get($validFavoriteScaleIds);

  if (!rawScales.length) {
    return [];
  }

  const favoriteSet = new Set(validFavoriteScaleIds);

  return rawScales.map((scale, index) => ({
    ...scale,
    shortName: `S${index + 1}`,
    isFavorite: favoriteSet.has(scale.id),
  }));
});

export const $favoriteScales = atom((get) => {
  const labeledScales = get($labeledScales);
  const validFavoriteScaleIds = get($validFavoriteScaleIds);

  if (validFavoriteScaleIds.length > 0) {
    return labeledScales.filter((it) => it.isFavorite);
  }

  return labeledScales.slice(0, MAX_FAVORITE_SCALES);
});

export const $currentScale = atom((get) => {
  const labeledScales = get($labeledScales);
  const currentScaleId = get($scaleId);

  return (
    labeledScales.find((it) => it.id === currentScaleId) ?? labeledScales[0]
  );
});

export const $displayedScales = atom((get) => {
  const favoriteScales = get($favoriteScales);
  const currentScale = get($currentScale);

  if (!currentScale) return favoriteScales;

  const isCurrentScaleFavorite = favoriteScales.some(
    (it) => it.id === currentScale.id,
  );

  if (isCurrentScaleFavorite) {
    return favoriteScales;
  }

  return [...favoriteScales, currentScale];
});

export const $canAddScaleToFavorite = atom((get) => {
  const validFavoriteScaleIds = get($validFavoriteScaleIds);
  return validFavoriteScaleIds.length < MAX_FAVORITE_SCALES;
});

export const maxFavoriteScales = MAX_FAVORITE_SCALES;
